import React, { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import './FireSafety.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faBell,
  faTint,
  faLightbulb,
  faDoorOpen,
  faFireExtinguisher,
  faCloud,
  faThermometerHalf,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { getAuthHeader, clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

// Import all safety components
import FireSafetyDashboard from './FireSafetyDashboard';
import FireAlarms from './FireAlarms/FireAlarms';
import Sprinklers from './Sprinklers/Sprinklers';
import EmergencyLights from './EmergencyLights/EmergencyLights';
import FireDoors from './FireDoors/FireDoors';
import FireExtinguishers from './FireExtinguishers/FireExtinguishers';
import SmokeDetectors from './SmokeDetectors/SmokeDetectors';
import HeatDetectors from './HeatDetectors/HeatDetectors';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';

const SHOWCASE_MODE = false;
const COMING_SOON_SECTIONS = [
  'fireAlarms', 'sprinklers', 'emergencyLights', 'fireDoors',
  'fireExtinguishers', 'smokeDetectors', 'heatDetectors'
];

const sidebarSections = [
  { id: 'dashboard',        title: 'Fire Dashboard',     icon: faChartLine,         component: FireSafetyDashboard },
  { id: 'fireAlarms',       title: 'Fire Alarms',        icon: faBell,              component: FireAlarms },
  { id: 'sprinklers',       title: 'Sprinklers',         icon: faTint,              component: Sprinklers },
  { id: 'emergencyLights',  title: 'Emergency Lights',   icon: faLightbulb,         component: EmergencyLights },
  { id: 'fireDoors',        title: 'Fire Doors',         icon: faDoorOpen,          component: FireDoors },
  { id: 'fireExtinguishers',title: 'Fire Extinguishers',icon: faFireExtinguisher,  component: FireExtinguishers },
  { id: 'smokeDetectors',   title: 'Smoke Detectors',    icon: faCloud,             component: SmokeDetectors },
  { id: 'heatDetectors',    title: 'Heat Detectors',     icon: faThermometerHalf,   component: HeatDetectors },
];

const SUB_NAV_ITEMS = sidebarSections.map(s => ({
  id: s.id,
  label: s.title,
  icon: <FontAwesomeIcon icon={s.icon} />
}));

const FireSafety = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeatureName, setComingSoonFeatureName] = useState('');

  const ctx = useOutletContext() || {};
  const activeSubSection = ctx.activeSubSection || 'dashboard';
  const setActiveSubSection = ctx.setActiveSubSection || (() => {});

  const handleSectionClick = (section) => {
    if (SHOWCASE_MODE && COMING_SOON_SECTIONS.includes(section)) {
      const sectionData = sidebarSections.find(s => s.id === section);
      setComingSoonFeatureName(sectionData ? sectionData.title : 'This Feature');
      setShowComingSoonModal(true);
      return;
    }
    setActiveSubSection(section);
  };

  const renderContent = () => {
    const section = sidebarSections.find(s => s.id === activeSubSection);
    if (!section?.component) {
      const dash = sidebarSections.find(s => s.id === 'dashboard');
      if (dash) { const C = dash.component; return <C propertyId={id} onNavigate={handleSectionClick} />; }
      return null;
    }
    if (activeSubSection === 'dashboard') {
      const Component = section.component;
      return <Component propertyId={id} onNavigate={handleSectionClick} />;
    }
    const Component = section.component;
    return <Component propertyId={id} />;
  };

  const visiblePills = activeSubSection === 'dashboard'
    ? SUB_NAV_ITEMS.filter(i => i.id === 'dashboard')
    : SUB_NAV_ITEMS;

  const pillNav = (
    <nav className="rs-subnav">
      {visiblePills.map((item) => (
        <button
          key={item.id}
          className={`rs-pill${activeSubSection === item.id ? ' rs-pill-active' : ''}`}
          onClick={() => handleSectionClick(item.id)}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className="fire-safety-container">
      <div className="rs-subnav-mobile-wrap">{pillNav}</div>
      <div className="fire-safety-content">
        <div className="fire-safety-main">
          <div className="rs-subnav-desktop-wrap">{pillNav}</div>
          {renderContent()}
        </div>
      </div>
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName={comingSoonFeatureName}
      />
    </div>
  );
};

export default FireSafety;
